/**
 * Producer Training Clip 2 — Controlling the Broadcast
 * Flow: Edit empty Finals heat → add riders → switch to Finals → Go Live.
 * Uses real Button component, real CSS classes, real ThemedOverlay.
 * 390 frames at 30fps = 13 seconds.
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { AutoCursor } from "../components/AutoCursor";
import { ScreenMock } from "../components/ScreenMock";
import { FadeTransition } from "../components/FadeTransition";
import { StepLabel } from "../components/StepLabel";
import { Button } from "../../components/ui/Button";
import { ThemedOverlay } from "../../overlay/ThemedOverlay";
import { OVERLAY_STYLES } from "../../overlay/styles";

const ACCENT = "#a855f7";
const BLUE = "#3b82f6";

type OvRider = { id: string; name: string; initials: string; colour: string; score: number | null; hits: number };

const HEAT1_RIDERS: OvRider[] = [
  { id: "1", name: "Harley Clifford", initials: "HC", colour: "#3b82f6", score: 8.5, hits: 0 },
  { id: "2", name: "Nic Rapa", initials: "NR", colour: "#22c55e", score: 7.2, hits: 0 },
  { id: "3", name: "Dallas Friday", initials: "DF", colour: "#f59e0b", score: 6.1, hits: 0 },
];
const HEAT1_SCORES: Record<string, Record<string, number>> = {
  "1": { "1": 8.0, "2": 9.0, "3": 8.5 },
  "2": { "1": 7.0, "2": 7.5, "3": 7.1 },
  "3": { "1": 6.0, "2": 6.5, "3": 5.8 },
};
const FINALS_RIDERS: OvRider[] = [
  { id: "1", name: "Harley Clifford", initials: "HC", colour: "#3b82f6", score: null, hits: 0 },
  { id: "2", name: "Nic Rapa", initials: "NR", colour: "#22c55e", score: null, hits: 0 },
  { id: "3", name: "Dallas Friday", initials: "DF", colour: "#f59e0b", score: null, hits: 0 },
];

interface CardRider {
  id: string; name: string; nickname: string;
  attemptAvgs: (number | null)[]; final: number | null;
  currentAttempt: number; maxAttempts: number;
  judges: (number | null)[][]; judgeNames: string[];
}

const J = ["Sarah", "Mike", "Lily"];

const HEAT1_CARDS: CardRider[] = [
  { id: "1", name: "Harley Clifford", nickname: "The King", attemptAvgs: [8.5, 7.9, null], final: 8.5, currentAttempt: 2, maxAttempts: 3, judgeNames: J,
    judges: [[8.0, 7.5, null], [9.0, 8.5, null], [8.5, 7.7, null]] },
  { id: "2", name: "Nic Rapa", nickname: "Raptor", attemptAvgs: [7.2, 6.8, null], final: 7.2, currentAttempt: 2, maxAttempts: 3, judgeNames: J,
    judges: [[7.0, 6.5, null], [7.5, 7.0, null], [7.1, 6.9, null]] },
  { id: "3", name: "Dallas Friday", nickname: "D-Fri", attemptAvgs: [6.1, null, null], final: 6.1, currentAttempt: 1, maxAttempts: 3, judgeNames: J,
    judges: [[6.0, null, null], [6.5, null, null], [5.8, null, null]] },
];

const FINALS_CARDS: CardRider[] = [
  { id: "1", name: "Harley Clifford", nickname: "The King", attemptAvgs: [null, null, null], final: null, currentAttempt: 1, maxAttempts: 3, judgeNames: J,
    judges: [[null, null, null], [null, null, null], [null, null, null]] },
  { id: "2", name: "Nic Rapa", nickname: "Raptor", attemptAvgs: [null, null, null], final: null, currentAttempt: 1, maxAttempts: 3, judgeNames: J,
    judges: [[null, null, null], [null, null, null], [null, null, null]] },
  { id: "3", name: "Dallas Friday", nickname: "D-Fri", attemptAvgs: [null, null, null], final: null, currentAttempt: 1, maxAttempts: 3, judgeNames: J,
    judges: [[null, null, null], [null, null, null], [null, null, null]] },
];

const HEATS = [
  { label: "Men's Open", heats: ["Heat 1", "Semi-Finals", "Finals"] },
  { label: "Women's Open", heats: ["Heat 1", "Finals"] },
];

export function ProducerCourse2() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={55}><Title /></Sequence>
      <Sequence from={45} durationInFrames={495}>
        <FadeTransition durationInFrames={495} fadeIn={10} fadeOut={10}><Demo /></FadeTransition>
      </Sequence>
      <Sequence from={535} durationInFrames={65}><Done /></Sequence>
    </AbsoluteFill>
  );
}

function Title() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 40, 55], [0, 1, 1, 0], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ fontSize: 10, color: ACCENT, textTransform: "uppercase", letterSpacing: 3, fontFamily: "'JetBrains Mono', monospace", marginBottom: 14 }}>Producer Training · Clip 2</div>
      <div style={{ fontSize: 48, fontWeight: 800, color: "#fff" }}>Controlling the Broadcast</div>
    </AbsoluteFill>
  );
}

function Demo() {
  const frame = useCurrentFrame();

  /*
   * Timeline (slower, 600 total):
   *   0-50:    OFF AIR on Heat 1 with scores, pause
   *   50-80:   Click Edit Heats → modal opens
   *   80-110:  Select Finals in modal heat selector
   *   110-220: Add 3 riders one by one (slower, ~35f each)
   *   220-260: Close modal
   *   260-330: Open dropdown → select Finals
   *   330-390: Click Go Live
   *   390-485: Breathe, show overlay working
   */

  const editModalOpen = frame >= 65 && frame < 240;
  const modalDropdownOpen = frame >= 85 && frame < 100; // dropdown inside modal
  const modalHeatSelected = frame >= 100; // switched to Finals in modal
  const ridersInFinals = frame < 130 ? 0 : frame < 160 ? 1 : frame < 190 ? 2 : 3;
  const dropdownOpen = frame >= 280 && frame < 330;
  const onFinals = frame >= 330;
  const isLive = frame >= 370;

  const heatLabel = onFinals ? "Finals" : "Heat 1";
  const overlayRiders = onFinals ? FINALS_RIDERS : HEAT1_RIDERS;
  const overlayScores = onFinals ? {} : HEAT1_SCORES;
  const cardRiders = onFinals ? FINALS_CARDS : HEAT1_CARDS;

  const PREVIEW_SCALE = 0.18;
  const previewW = 1920 * PREVIEW_SCALE;
  const previewH = 1080 * PREVIEW_SCALE;

  // Modal rider lists (only populated once Finals is selected in modal)
  const allAvailable = ["Harley Clifford", "Nic Rapa", "Dallas Friday"];
  const inHeat = modalHeatSelected ? allAvailable.slice(0, ridersInFinals) : [];
  const available = modalHeatSelected ? allAvailable.slice(ridersInFinals) : [];

  return (
    <AbsoluteFill>
      <ScreenMock url="raley.koda-software.com/competition/boardstock/producer">
        <div style={{ width: "100%", height: "100%", background: "var(--c-bg, #0a0a0b)", fontFamily: "'Inter', sans-serif", color: "var(--c-text, #e4e4e7)", display: "flex", flexDirection: "column", position: "relative" }}>
          {/* Nav */}
          <div style={{ height: 44, background: "var(--c-surface, #111113)", borderBottom: "1px solid var(--c-border, #2a2a32)", display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0 }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: 14, letterSpacing: 2 }}>RALEY</span>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--c-text, #e4e4e7)" }}>COMPETITIONS</span>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--c-text-muted, #71717a)" }}>RIDERS</span>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--c-text-muted, #71717a)" }}>TRAINING</span>
            <span style={{ marginLeft: "auto", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--c-text-muted, #71717a)" }}>Takoda Mundy</span>
          </div>

          <div style={{ padding: "16px 24px", flex: 1, overflow: "hidden" }}>
            {/* Header */}
            <div className="page-header">
              <div>
                <h1 className="page-title" style={{ margin: 0 }}>TAKODA MUNDY</h1>
                <div className="flex gap-3 items-center" style={{ marginTop: 2 }}>
                  <span className="text-sm text-muted">Boardstock</span>
                </div>
                <div className="flex gap-3 items-center" style={{ marginTop: 4, position: "relative" }}>
                  {isLive
                    ? <span className="badge badge-live">LIVE</span>
                    : <span className="badge badge-off">OFF AIR</span>}
                  <div id="heat-dropdown" style={{
                    minWidth: 180, fontFamily: "var(--font-sans)", fontSize: 14,
                    padding: "6px 12px", background: "var(--c-surface, #111113)",
                    border: "1px solid var(--c-border, #2a2a32)", borderRadius: 6,
                    color: "var(--c-text, #e4e4e7)", cursor: "pointer",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <span>{heatLabel}</span>
                    <span style={{ fontSize: 10, color: "var(--c-text-muted)" }}>▾</span>
                  </div>
                  <Button variant="secondary" size="sm" id="edit-heats-btn">Edit Heats</Button>
                  {isLive
                    ? <Button variant="danger" size="sm">Go Dark</Button>
                    : <Button size="sm" id="go-live-btn">Go Live</Button>}

                  {/* Dropdown */}
                  {dropdownOpen && (
                    <div style={{
                      position: "absolute", top: 38, left: 80,
                      background: "var(--c-surface, #111113)", border: "1px solid var(--c-border, #2a2a32)",
                      borderRadius: 6, padding: 4, zIndex: 50, minWidth: 200, boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
                    }}>
                      {HEATS.map((g) => {
                        // Only highlight the current heat in its own division
                        const isCurrentDivision = g.label === "Men's Open";
                        return (
                          <div key={g.label}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: "var(--c-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", padding: "6px 8px 2px" }}>{g.label}</div>
                            {g.heats.map((h) => {
                              const isSelected = isCurrentDivision && h === heatLabel;
                              return (
                                <div key={h} id={g.label === "Men's Open" && h === "Finals" ? "dropdown-finals" : undefined} style={{
                                  padding: "6px 8px", fontSize: 13, cursor: "pointer",
                                  background: isSelected ? "var(--c-accent, #3b82f6)" : "transparent",
                                  color: isSelected ? "#fff" : "var(--c-text, #e4e4e7)", borderRadius: 4,
                                }}>{h}</div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Overlay controls */}
            <div className="flex gap-3" style={{ marginBottom: 24 }}>
              <Button variant="primary" size="sm">Hide Heat Name</Button>
              <Button variant="secondary" size="sm">Show Heat Leaderboard</Button>
              <Button variant="primary" size="sm">Hide Heat Riders</Button>
              <Button variant="secondary" size="sm">Show Rider Scores</Button>
              <Button variant="secondary" size="sm">Clear Focus</Button>
            </div>

            {/* Overlay style */}
            <div style={{ marginBottom: 24 }}>
              <div className="field-label" style={{ marginBottom: 8 }}>Overlay Style</div>
              <div className="flex gap-2">
                {["Steel", "Olympic", "Broadcast", "Neon", "Clean"].map((s, i) => (
                  <button key={s} className={`btn btn-sm ${i === 0 ? "btn-primary" : "btn-secondary"}`}>{s}</button>
                ))}
              </div>
            </div>

            {/* Rider cards */}
            <div className="control-grid">
              {cardRiders.map((r) => {
                const validAvgs = r.attemptAvgs.filter((v): v is number => v !== null);
                const bestAvg = validAvgs.length > 0 ? Math.max(...validAvgs) : null;
                return (
                  <div key={r.id} className="rider-control-card">
                    <div className="rider-name">{r.name}</div>
                    <div className="rider-meta">{r.nickname}</div>

                    {/* Per-attempt average boxes */}
                    <div style={{ display: "flex", gap: 4, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
                      {r.attemptAvgs.map((avg, i) => {
                        const isBest = avg !== null && bestAvg !== null && avg === bestAvg;
                        return (
                          <div key={i} style={{
                            background: isBest ? "var(--c-accent)" : "var(--c-surface-2, #1e1e1e)",
                            border: `1px solid ${isBest ? "var(--c-accent)" : "var(--c-border)"}`,
                            borderRadius: 3, padding: "1px 5px", fontSize: 10,
                            fontFamily: "var(--font-mono, monospace)",
                            color: isBest ? "#fff" : avg != null ? "var(--c-text)" : "var(--c-text-muted, #666)",
                            fontWeight: isBest ? 700 : 400, minWidth: 24, textAlign: "center",
                          }}>
                            {avg != null ? avg.toFixed(1) : "-"}
                          </div>
                        );
                      })}
                      <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "var(--font-mono, monospace)", color: r.final != null ? "var(--c-accent)" : "var(--c-text-muted, #666)", marginLeft: 2 }}>
                        {r.final != null ? r.final.toFixed(1) : "-"}
                      </div>
                    </div>

                    {/* Attempt dots */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, marginBottom: 4 }}>
                      <button className="btn btn-ghost btn-sm" style={{ padding: "2px 6px", fontSize: 11 }}>-</button>
                      <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                        {Array.from({ length: r.maxAttempts }, (_, i) => {
                          const n = i + 1;
                          const active = n <= r.currentAttempt;
                          const current = n === r.currentAttempt;
                          return <div key={n} style={{ width: 8, height: 8, borderRadius: "50%", background: active ? "var(--c-accent)" : "transparent", border: `1px solid ${active ? "var(--c-accent)" : "var(--c-border)"}`, boxShadow: current ? "0 0 0 2px var(--c-accent)" : "none", opacity: active ? 1 : 0.3 }} />;
                        })}
                      </div>
                      <button className="btn btn-ghost btn-sm" style={{ padding: "2px 6px", fontSize: 11 }}>+</button>
                      <span className="text-mono text-muted" style={{ fontSize: 10 }}>{r.currentAttempt}/{r.maxAttempts}</span>
                    </div>

                    <div className="rider-actions">
                      <Button variant="ghost" size="sm">Focus</Button>
                      <Button variant="ghost" size="sm">OBS</Button>
                      <Button variant="ghost" size="sm">Scores</Button>
                    </div>

                    {/* Score grid */}
                    <div style={{ marginTop: 8, borderTop: "1px solid var(--c-border)", paddingTop: 8 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10, fontFamily: "var(--font-mono, monospace)" }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left", padding: "2px 4px", color: "var(--c-text-muted)", fontWeight: 600 }}></th>
                            {Array.from({ length: r.maxAttempts }, (_, a) => (
                              <th key={a} style={{ textAlign: "center", padding: "2px 4px", color: "var(--c-text-muted)", fontWeight: 600 }}>A{a + 1}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {r.judges.map((row, j) => (
                            <tr key={j}>
                              <td style={{ padding: "2px 4px", color: "var(--c-text-muted)", fontWeight: 600 }}>{r.judgeNames[j]}</td>
                              {row.map((v, a) => (
                                <td key={a} style={{ textAlign: "center", padding: "2px 4px", color: v != null ? "var(--c-text)" : "var(--c-text-muted)" }}>
                                  {v != null ? v.toFixed(1) : "-"}
                                </td>
                              ))}
                            </tr>
                          ))}
                          <tr style={{ borderTop: "1px solid var(--c-border)" }}>
                            <td style={{ padding: "3px 4px", fontWeight: 700, color: "var(--c-text-muted)" }}>Final</td>
                            {r.attemptAvgs.map((avg, a) => (
                              <td key={a} style={{ textAlign: "center", padding: "3px 4px", fontWeight: 700, color: avg != null ? "var(--c-accent)" : "var(--c-text-muted)" }}>
                                {avg != null ? avg.toFixed(1) : "-"}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                      {r.final != null && (
                        <div style={{ marginTop: 4, fontSize: 11, fontWeight: 700, color: "var(--c-accent)", fontFamily: "var(--font-mono, monospace)" }}>
                          Overall: {r.final.toFixed(1)} (max)
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* OBS Preview — always visible */}
          <div style={{
            position: "absolute", bottom: 16, right: 16,
            width: previewW, height: previewH, zIndex: 800,
            overflow: "hidden", borderRadius: 4,
            border: "1px solid var(--c-border, #2a2a32)", background: "#000",
          }}>
            <div style={{ transform: `scale(${PREVIEW_SCALE})`, transformOrigin: "top left", width: 1920, height: 1080 }}>
              {isLive ? (
                <ThemedOverlay
                  theme={OVERLAY_STYLES.steel}
                  heatName={`Men's Open — ${heatLabel}`}
                  competitionName="Boardstock"
                  riders={overlayRiders}
                  showHeatBanner={true}
                  showHeatScores={true}
                  showLeaderboard={false}
                  judgeCount={3}
                  allScores={overlayScores}
                  isFreeTier={true}
                />
              ) : (
                <div style={{ width: 1920, height: 1080 }} />
              )}
            </div>
          </div>

          {/* Edit Heats modal */}
          {editModalOpen && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
              <div style={{ background: "var(--c-surface, #111113)", border: "1px solid var(--c-border, #2a2a32)", borderRadius: 8, width: 420, display: "flex", flexDirection: "column" }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--c-border, #2a2a32)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>Edit Heats</div>
                  <span id="close-modal" style={{ color: "var(--c-text-muted)", fontSize: 18, cursor: "pointer" }}>&times;</span>
                </div>
                <div style={{ padding: "12px 20px" }}>
                  {/* Heat selector inside modal — custom dropdown so it visually opens */}
                  <div style={{ position: "relative" }}>
                    <div
                      id="modal-heat-select"
                      style={{
                        width: "100%", padding: "8px 10px",
                        background: "var(--c-surface-2, #1a1a1e)", border: "1px solid var(--c-border, #2a2a32)",
                        borderRadius: 6, color: "var(--c-text, #e4e4e7)", fontSize: 13,
                        fontFamily: "var(--font-sans)", cursor: "pointer",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                      }}
                    >
                      <span>{modalHeatSelected ? "Finals" : "Heat 1"}</span>
                      <span style={{ fontSize: 10, color: "var(--c-text-muted)" }}>▾</span>
                    </div>
                    {modalDropdownOpen && (
                      <div style={{
                        position: "absolute", top: 40, left: 0, right: 0,
                        background: "var(--c-surface, #111113)", border: "1px solid var(--c-border, #2a2a32)",
                        borderRadius: 6, padding: 4, zIndex: 50, boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: "var(--c-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", padding: "6px 8px 2px" }}>Men&apos;s Open</div>
                        {["Heat 1", "Semi-Finals", "Finals"].map((h) => (
                          <div key={h} id={h === "Finals" ? "modal-select-finals" : undefined} style={{
                            padding: "6px 8px", fontSize: 13, cursor: "pointer", borderRadius: 4,
                            background: h === "Heat 1" ? "var(--c-accent, #3b82f6)" : "transparent",
                            color: h === "Heat 1" ? "#fff" : "var(--c-text, #e4e4e7)",
                          }}>{h}</div>
                        ))}
                        <div style={{ fontSize: 10, fontWeight: 600, color: "var(--c-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", padding: "6px 8px 2px" }}>Women&apos;s Open</div>
                        {["Heat 1", "Finals"].map((h) => (
                          <div key={`w-${h}`} style={{
                            padding: "6px 8px", fontSize: 13, cursor: "pointer", borderRadius: 4,
                            color: "var(--c-text, #e4e4e7)",
                          }}>{h}</div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="text-sm text-muted" style={{ marginTop: 8, fontSize: 11, lineHeight: 1.4 }}>
                    Removing a rider deletes all their scores in that heat.
                  </div>

                  {!modalHeatSelected && (
                    <div style={{ padding: "20px 0", fontSize: 12, color: "var(--c-text-muted)", textAlign: "center" }}>
                      Select a heat to manage riders
                    </div>
                  )}

                  {modalHeatSelected && inHeat.length > 0 && (
                    <>
                      <div className="text-sm text-muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6, marginTop: 12, fontWeight: 600 }}>
                        In Heat ({inHeat.length})
                      </div>
                      {inHeat.map((name) => (
                        <div key={name} className="flex items-center justify-between" style={{ padding: "5px 0" }}>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>{name}</span>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: "2px 8px", color: "var(--c-danger, #ef4444)" }}>Remove</button>
                        </div>
                      ))}
                    </>
                  )}

                  {modalHeatSelected && available.length > 0 && (
                    <>
                      <div className="text-sm text-muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 12, marginBottom: 6, fontWeight: 600 }}>
                        Available ({available.length})
                      </div>
                      {available.map((name, i) => (
                        <div key={name} className="flex items-center justify-between" style={{ padding: "5px 0" }}>
                          <span style={{ fontSize: 13 }}>{name}</span>
                          <button id={i === 0 ? "add-rider" : undefined} className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: "2px 8px" }}>Add</button>
                        </div>
                      ))}
                    </>
                  )}

                  {modalHeatSelected && available.length === 0 && (
                    <div style={{ padding: "8px 0", fontSize: 11, color: "var(--c-text-muted)", fontStyle: "italic" }}>All riders added</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <AutoCursor points={[
          { x: 500, y: 300, frame: 0 },
          // Open Edit Heats
          { target: "edit-heats-btn", frame: 35 },
          { target: "edit-heats-btn", frame: 65, click: true },
          // Open modal heat dropdown
          { target: "modal-heat-select", frame: 78 },
          { target: "modal-heat-select", frame: 85, click: true },
          // Select Finals in the dropdown
          { target: "modal-select-finals", frame: 93 },
          { target: "modal-select-finals", frame: 100, click: true },
          // Add first rider
          { target: "add-rider", frame: 118 },
          { target: "add-rider", frame: 130, click: true },
          // Add second rider
          { target: "add-rider", frame: 148 },
          { target: "add-rider", frame: 160, click: true },
          // Add third rider
          { target: "add-rider", frame: 178 },
          { target: "add-rider", frame: 190, click: true },
          // Close modal
          { target: "close-modal", frame: 225 },
          { target: "close-modal", frame: 240, click: true },
          // Open dropdown to switch to Finals
          { target: "heat-dropdown", frame: 265 },
          { target: "heat-dropdown", frame: 280, click: true },
          // Select Finals in dropdown
          { target: "dropdown-finals", frame: 310 },
          { target: "dropdown-finals", frame: 330, click: true },
          // Go Live
          { target: "go-live-btn", frame: 350 },
          { target: "go-live-btn", frame: 370, click: true },
          // Let it breathe
          { x: 500, y: 400, frame: 420 },
          { x: 500, y: 400, frame: 485 },
        ]} />
      </ScreenMock>

      <StepLabel text="Open Edit Heats to build the Finals lineup" show={5} hide={75} color={ACCENT} />
      <StepLabel text="Select the Finals heat in the modal" show={78} hide={115} color="#eab308" />
      <StepLabel text="Add riders who made it through — one by one" show={118} hide={210} color={BLUE} />
      <StepLabel text="Switch to the Finals heat on the main page" show={250} hide={340} color="#eab308" />
      <StepLabel text="Go Live — the overlay activates with fresh scores" show={345} hide={430} color="#22c55e" />
    </AbsoluteFill>
  );
}

function Done() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}><span style={{ fontSize: 24, color: "#fff" }}>✓</span></div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>Finals are ready — you're live</div>
    </AbsoluteFill>
  );
}
